import React, { useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { TipoTarea, Prioridad, EstadoTarea, PRIORIDAD_LABELS, ESTADO_LABELS } from '../types';

interface FormularioRegistroProps {
    onClose: () => void;
    onSuccess: () => void;
    clienteIdInicial?: number;
    estadoInicial?: EstadoTarea;
}

const tipoOptions = [
    { value: 'bug_soporte', label: 'Bug / Soporte', icon: '🐛', color: 'red' },
    { value: 'nuevo_modulo', label: 'Nuevo Módulo', icon: '🚀', color: 'green' },
    { value: 'actualizacion_refactor', label: 'Refactor', icon: '🔄', color: 'blue' }
];

const prioridadOptions: Array<{ value: Prioridad; color: string; emoji: string }> = [
    { value: 'urgente',  color: 'red',    emoji: '🔥' },
    { value: 'alta',     color: 'orange', emoji: '!' },
    { value: 'moderada', color: 'amber',  emoji: '~' },
    { value: 'baja',     color: 'slate',  emoji: '–' }
];

const todayStr = (): string => new Date().toISOString().split('T')[0];

export const FormularioRegistro: React.FC<FormularioRegistroProps> = ({ onClose, onSuccess, clienteIdInicial, estadoInicial }) => {
    const { clientes, registrarPedidoCliente } = useKanban();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        cliente_id: clienteIdInicial ? String(clienteIdInicial) : '',
        titulo: '',
        descripcion: '',
        tipo_solicitud: 'bug_soporte' as TipoTarea,
        prioridad: 'moderada' as Prioridad,
        fecha_limite: '',
        estado: estadoInicial || 'backlog' as EstadoTarea
    });
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!formData.cliente_id || !formData.titulo) {
            setError('Cliente y título son obligatorios');
            return;
        }

        setLoading(true);
        const success = await registrarPedidoCliente({
            cliente_id: parseInt(formData.cliente_id, 10),
            titulo: formData.titulo,
            descripcion: formData.descripcion || undefined,
            tipo_solicitud: formData.tipo_solicitud,
            prioridad: formData.prioridad,
            fecha_limite: formData.fecha_limite || null,
            estado: formData.estado
        });

        setLoading(false);

        if (success) {
            onSuccess();
            onClose();
        } else {
            setError('Error al registrar la tarea. Intenta de nuevo.');
        }
    };

    const selectedCliente = clientes.find(c => c.id.toString() === formData.cliente_id);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Nueva Tarea Rápida</h2>
                            <p className="text-xs text-slate-500">Registra un nuevo pedido o incidencia</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-5">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Cliente *</label>
                        <div className="relative">
                            <select
                                value={formData.cliente_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
                                required
                            >
                                <option value="">Seleccionar cliente...</option>
                                {clientes.map(cliente => (
                                    <option key={cliente.id} value={cliente.id}>
                                        {cliente.empresa} - {cliente.nombre}
                                    </option>
                                ))}
                            </select>
                            <div
                                className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full pointer-events-none"
                                style={{ backgroundColor: selectedCliente?.color || '#64748b' }}
                            ></div>
                            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Solicitud</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {tipoOptions.map(tipo => (
                                <button
                                    key={tipo.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, tipo_solicitud: tipo.value as TipoTarea }))}
                                    className={`
                                        flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all
                                        ${formData.tipo_solicitud === tipo.value
                                            ? tipo.color === 'red' ? 'border-red-500 bg-red-50' :
                                              tipo.color === 'green' ? 'border-green-500 bg-green-50' :
                                              'border-blue-500 bg-blue-50'
                                            : 'border-slate-200 hover:border-slate-300 bg-white'
                                        }
                                    `}
                                >
                                    <span className="text-xl">{tipo.icon}</span>
                                    <span className={`text-xs font-medium ${
                                        formData.tipo_solicitud === tipo.value
                                            ? tipo.color === 'red' ? 'text-red-700' :
                                              tipo.color === 'green' ? 'text-green-700' :
                                              'text-blue-700'
                                            : 'text-slate-600'
                                    }`}>
                                        {tipo.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Prioridad</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {prioridadOptions.map(p => {
                                const isActive = formData.prioridad === p.value;
                                const activeClass =
                                    p.color === 'red'    ? 'border-red-500 bg-red-50 text-red-700' :
                                    p.color === 'orange' ? 'border-orange-500 bg-orange-50 text-orange-700' :
                                    p.color === 'amber'  ? 'border-amber-500 bg-amber-50 text-amber-700' :
                                                          'border-slate-500 bg-slate-100 text-slate-700';
                                return (
                                    <button
                                        key={p.value}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, prioridad: p.value }))}
                                        className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all ${
                                            isActive ? activeClass : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600'
                                        }`}
                                        title={PRIORIDAD_LABELS[p.value]}
                                    >
                                        <span className="text-base font-bold">{p.emoji}</span>
                                        <span className="text-[10px] font-semibold uppercase tracking-wider">
                                            {PRIORIDAD_LABELS[p.value]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Título *</label>
                        <input
                            type="text"
                            value={formData.titulo}
                            onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                            placeholder="Describe brevemente el problema o solicitud"
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                        <textarea
                            value={formData.descripcion}
                            onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                            placeholder="Detalles adicionales, pasos para reproducir, logs, etc."
                            rows={3}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <span>📅 Fecha de entrega</span>
                            <span className="text-[11px] text-slate-500 font-normal">(opcional)</span>
                        </label>
                        <input
                            type="date"
                            value={formData.fecha_limite}
                            min={todayStr()}
                            onChange={(e) => setFormData(prev => ({ ...prev, fecha_limite: e.target.value }))}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                        <p className="text-[11px] text-slate-500 mt-1.5">
                            Se mostrará en el calendario y contará los días hasta el deadline
                        </p>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                    Registrando...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Registrar Tarea
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};